import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar as CalendarIcon, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const toTodayIso = () => {
  try {
    return new Date().toLocaleDateString('en-CA');
  } catch (_) {
    return new Date().toISOString().slice(0, 10);
  }
};

const pad2 = (n) => String(n).padStart(2, '0');

const isoFromDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return toTodayIso();
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
};

const dateFromIso = (iso) => {
  const s = String(iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (![y, mo, d].every((n) => Number.isFinite(n))) return null;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return new Date(y, mo - 1, d);
};

const formatIdr = (value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return `Rp ${safe.toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
};

const getPaymentMethodLabel = (payment) => {
  const method = String(payment?.method || '').toLowerCase();
  if (!method) return '-';
  if (method === 'cash') return 'Tunai';
  if (method === 'qris') return 'QRIS';
  if (method.startsWith('transfer')) return 'Transfer';
  return payment?.method || '-';
};

const getPaymentStatusLabel = (payment) => {
  const status = String(payment?.status || '').toLowerCase();
  if (!status) return '-';
  if (status === 'verified') return 'Verified';
  if (status === 'pending') return 'Pending';
  if (status === 'rejected') return 'Rejected';
  return payment?.status || '-';
};

const getTotalAmount = (bookingWithDetails) => {
  const computedAny =
    bookingWithDetails?.payment?.total_amount ??
    bookingWithDetails?.payment?.totalAmount ??
    bookingWithDetails?.computed_total_amount ??
    bookingWithDetails?.computedTotalAmount;
  const computed = Number(computedAny);
  return Number.isFinite(computed) ? computed : 0;
};

const generateTimeSlots = (openHour, closeHour) => {
  const open = Number.isFinite(openHour) ? openHour : 8;
  const close = Number.isFinite(closeHour) ? closeHour : 22;
  const openH = Math.max(0, Math.min(23, Math.floor(open)));
  let closeH = Math.max(0, Math.min(23, Math.floor(close)));
  if (closeH <= openH) closeH += 24;
  const slots = [];
  for (let h = openH; h < closeH; h += 1) {
    const start = h % 24;
    const end = (h + 1) % 24;
    slots.push(`${pad2(start)}:00-${pad2(end)}:00`);
  }
  return slots;
};

const normalizeSlots = (timeSlots) => {
  if (Array.isArray(timeSlots)) return timeSlots.filter(Boolean);
  if (typeof timeSlots === 'string') {
    return timeSlots
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const parseTimeToSeconds = (value) => {
  const s = String(value || '').trim();
  if (!s) return null;
  const parts = s.split(':').map((p) => p.trim());
  if (parts.length < 2) return null;
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  const ss = parts.length >= 3 ? Number(parts[2]) : 0;
  if (![hh, mm, ss].every((n) => Number.isFinite(n))) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59 || ss < 0 || ss > 59) return null;
  return hh * 3600 + mm * 60 + ss;
};

const parseSlotRangeSeconds = (slot) => {
  const s = String(slot || '').trim();
  const [startRaw, endRaw] = s.split('-').map((x) => x.trim());
  if (!startRaw || !endRaw) return null;
  const startSec = parseTimeToSeconds(startRaw);
  const endSec = parseTimeToSeconds(endRaw);
  if (startSec == null || endSec == null) return null;
  return { startSec, endSec };
};

const AdminLaporanLapangan = () => {
  const navigate = useNavigate();
  const [reportDate, setReportDate] = useState(() => toTodayIso());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [bookingsByVenueId, setBookingsByVenueId] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const [slotSnapshots, setSlotSnapshots] = useState({ loading: false, items: [], error: null });

  const clearSlotSnapshots = useCallback(() => {
    setSlotSnapshots({ loading: false, items: [], error: null });
  }, []);

  const displayedVenues = useMemo(() => {
    const list = Array.isArray(venues) ? venues : [];
    const prioritized = list.filter((v) => /padel/i.test(v?.name || '') || /futsal/i.test(v?.name || ''));
    const base = prioritized.length ? prioritized : list;
    return [...base].sort((a, b) => {
      const an = (a?.name || '').toLowerCase();
      const bn = (b?.name || '').toLowerCase();
      const aRank = /padel/.test(an) ? 0 : /futsal/.test(an) ? 1 : 2;
      const bRank = /padel/.test(bn) ? 0 : /futsal/.test(bn) ? 1 : 2;
      if (aRank !== bRank) return aRank - bRank;
      return an.localeCompare(bn);
    });
  }, [venues]);

  const slotIndexByVenueId = useMemo(() => {
    const out = {};
    for (const v of displayedVenues) {
      const items = Array.isArray(bookingsByVenueId[v.id]) ? bookingsByVenueId[v.id] : [];
      const index = {};
      for (const item of items) {
        const paymentStatus = (item?.payment?.status || '').toLowerCase();
        if (paymentStatus !== 'verified') continue;
        const booking = item?.booking;
        for (const slot of normalizeSlots(booking?.time_slots)) {
          if (!index[slot]) index[slot] = item;
        }
      }
      out[v.id] = index;
    }
    return out;
  }, [displayedVenues, bookingsByVenueId]);

  const fetchSlotSnapshots = useCallback(
    async (venueId, slot) => {
      if (!venueId || !slot) return;
      const range = parseSlotRangeSeconds(slot);
      if (!range) {
        setSlotSnapshots({ loading: false, items: [], error: 'Format slot tidak valid' });
        return;
      }
      setSlotSnapshots((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const res = await api.get(`/admin/cctv/history/${venueId}`, { params: { date: reportDate } });
        const all = Array.isArray(res?.data?.items) ? res.data.items : [];
        const filtered = all.filter((it) => {
          const t = parseTimeToSeconds(it?.time);
          if (t == null) return false;
          if (range.endSec > range.startSec) {
            return t >= range.startSec && t < range.endSec;
          }
          return t >= range.startSec || t < range.endSec;
        });
        setSlotSnapshots({ loading: false, items: filtered, error: null });
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Gagal memuat snapshot slot';
        setSlotSnapshots({ loading: false, items: [], error: msg });
      }
    },
    [reportDate]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const venuesRes = await api.get('/venues');
      const list = Array.isArray(venuesRes.data) ? venuesRes.data : [];
      setVenues(list);

      const prioritized = list.filter((v) => /padel/i.test(v?.name || '') || /futsal/i.test(v?.name || ''));
      const targetVenues = prioritized.length ? prioritized : list;

      const responses = await Promise.all(
        targetVenues.map((v) =>
          api.get('/kasir/bookings', { params: { booking_date: reportDate, venue_id: v.id } }).then((r) => ({
            venueId: v.id,
            data: r.data,
          }))
        )
      );

      const nextMap = {};
      for (const r of responses) {
        nextMap[r.venueId] = Array.isArray(r.data) ? r.data : [];
      }
      setBookingsByVenueId(nextMap);
    } catch (_) {
      toast.error('Gagal memuat laporan lapangan');
    } finally {
      setLoading(false);
    }
  }, [reportDate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        toast.error('Akses ditolak');
        navigate('/admin/login');
        return;
      }
    } catch (_) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    loadAll();
  }, [navigate, loadAll]);

  useEffect(() => {
    if (!showDialog) return;
    const venueId = selectedSlot?.venue?.id;
    const slot = selectedSlot?.slot;
    if (!venueId || !slot) return;
    fetchSlotSnapshots(venueId, slot);
  }, [showDialog, selectedSlot, fetchSlotSnapshots]);

  const onDateChange = (nextIso) => {
    const next = String(nextIso || '').trim();
    setShowDialog(false);
    setSelectedSlot(null);
    clearSlotSnapshots();
    setReportDate(next || toTodayIso());
  };

  const selectedCalendarDate = useMemo(() => dateFromIso(reportDate), [reportDate]);

  const onCalendarSelect = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
    setCalendarOpen(false);
    onDateChange(isoFromDate(date));
  };

  const openSlotDetail = (venue, slot) => {
    const match = slotIndexByVenueId?.[venue?.id]?.[slot] || null;
    setSelectedSlot({ venue, slot, bookingWithDetails: match });
    setShowDialog(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div className="flex flex-col">
                <span className="text-xl font-heading font-bold italic text-white uppercase">Laporan Lapangan</span>
                <span className="text-xs text-zinc-500">Tanggal: {reportDate}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 hover:border-primary/50">
                    <CalendarIcon className="w-4 h-4 text-zinc-300" />
                    <span className="font-mono text-sm">{reportDate}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="bg-zinc-900 border border-zinc-800 p-0">
                  <Calendar mode="single" selected={selectedCalendarDate} onSelect={onCalendarSelect} initialFocus />
                </PopoverContent>
              </Popover>
              <button
                onClick={loadAll}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-white hover:border-primary/50 disabled:opacity-50"
              >
                <RefreshCcw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : displayedVenues.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500 text-lg">Lapangan tidak ditemukan</p>
            </div>
          ) : (
            <div className="space-y-10">
              {displayedVenues.map((venue) => {
                const openHour = Number(venue?.open_hour);
                const closeHour = Number(venue?.close_hour);
                const slots = generateTimeSlots(
                  Number.isFinite(openHour) ? openHour : 8,
                  Number.isFinite(closeHour) ? closeHour : 22
                );
                const index = slotIndexByVenueId?.[venue.id] || {};

                return (
                  <div key={venue.id} className="glass glow-border rounded-3xl p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
                      <div>
                        <h2 className="text-2xl font-heading font-black uppercase text-white">{venue?.name}</h2>
                        <p className="text-sm text-white/50">
                          Operasional: {pad2(Number.isFinite(openHour) ? openHour : 8)}:00 - {pad2(Number.isFinite(closeHour) ? closeHour : 22)}:00
                        </p>
                      </div>
                      <div className="text-xs text-white/50">
                        Slot: <span className="text-white font-bold">{slots.length}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {slots.map((slot) => {
                        const bookingWithDetails = index[slot] || null;
                        const isBooked = Boolean(bookingWithDetails);
                        return (
                          <button
                            key={slot}
                            onClick={() => openSlotDetail(venue, slot)}
                            className={[
                              'rounded-xl border px-3 py-3 text-left transition-all',
                              isBooked
                                ? 'bg-primary text-black border-primary shadow-[0_0_0_1px_rgba(206,255,5,0.35),0_0_24px_rgba(206,255,5,0.12)]'
                                : 'bg-zinc-900/60 text-zinc-200 border-zinc-800 hover:border-primary/50',
                            ].join(' ')}
                          >
                            <div className={['text-sm font-black', isBooked ? 'text-black' : 'text-white'].join(' ')}>{slot}</div>
                            <div className={['text-xs font-bold uppercase mt-1', isBooked ? 'text-black/80' : 'text-zinc-400'].join(' ')}>
                              {isBooked ? 'Booked' : 'Kosong'}
                            </div>
                            {isBooked && (
                              <div className="text-[11px] font-semibold mt-2 text-black/80 line-clamp-1">
                                {bookingWithDetails?.booking?.customer_name || '-'}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setSelectedSlot(null);
            clearSlotSnapshots();
          }
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase">Detail Slot</DialogTitle>
          </DialogHeader>

          {selectedSlot && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-zinc-400">Lapangan</div>
                  <div className="text-white font-bold">{selectedSlot.venue?.name}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Tanggal</div>
                  <div className="text-white font-bold">{reportDate}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Slot</div>
                  <div className="text-white font-bold">{selectedSlot.slot}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Status</div>
                  <div className="text-white font-bold">{selectedSlot.bookingWithDetails ? 'Booked' : 'Kosong'}</div>
                </div>
              </div>

              {selectedSlot.bookingWithDetails ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-zinc-400">Nama</div>
                    <div className="text-white font-bold">{selectedSlot.bookingWithDetails.booking?.customer_name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-zinc-400">No. HP</div>
                    <div className="text-white font-bold">{selectedSlot.bookingWithDetails.booking?.customer_phone || '-'}</div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-sm text-zinc-400">Booking ID</div>
                    <div className="text-white font-mono font-bold">
                      {selectedSlot.bookingWithDetails.booking?.id ? selectedSlot.bookingWithDetails.booking.id.slice(0, 8) : '-'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-400">Slot masih kosong</div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-zinc-800 pt-4">
                <div>
                  <div className="text-sm text-zinc-400">Total Harga</div>
                  <div className="text-white font-bold">
                    {selectedSlot.bookingWithDetails ? formatIdr(getTotalAmount(selectedSlot.bookingWithDetails)) : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Metode Bayar</div>
                  <div className="text-white font-bold">
                    {selectedSlot.bookingWithDetails ? getPaymentMethodLabel(selectedSlot.bookingWithDetails.payment) : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Status Pembayaran</div>
                  <div className="text-white font-bold">
                    {selectedSlot.bookingWithDetails ? getPaymentStatusLabel(selectedSlot.bookingWithDetails.payment) : '-'}
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-bold uppercase text-white">Snapshot Slot</div>
                  <button
                    onClick={() => fetchSlotSnapshots(selectedSlot.venue?.id, selectedSlot.slot)}
                    disabled={slotSnapshots.loading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                  >
                    <RefreshCcw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>

                {slotSnapshots.loading ? (
                  <div className="text-center py-10">
                    <div className="inline-block w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : slotSnapshots.error ? (
                  <div className="text-sm text-red-300">{slotSnapshots.error}</div>
                ) : (slotSnapshots.items || []).length ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {slotSnapshots.items.map((it, idx) => (
                      <div key={`${it?.time || 't'}-${idx}`} className="rounded-lg border border-zinc-800 bg-black/20 overflow-hidden">
                        <div className="px-3 py-2 text-xs text-zinc-300 border-b border-zinc-800 flex items-center justify-between">
                          <span className="font-mono">{it?.time || '-'}</span>
                          <span className="text-zinc-500">{selectedSlot.slot}</span>
                        </div>
                        <div className="aspect-video bg-black flex items-center justify-center">
                          {it?.image_url ? (
                            <img src={it.image_url} alt={`Snapshot ${it.time || ''}`} className="w-full h-full object-contain" />
                          ) : (
                            <div className="text-zinc-500 text-sm px-4 text-center">Snapshot tidak tersedia</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-zinc-400">Tidak ada snapshot pada jam slot ini</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLaporanLapangan;
